import { Card, Col, Flex, Row } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import type { RoleDetailPageViewProps } from './types';
import { RoleDetailHero } from './components/RoleDetailHero/RoleDetailHero';
import { RolePermissionsAccordion } from './components/RolePermissionsAccordion/RolePermissionsAccordion';

export function RoleDetailPageView({
  organizationId,
  useSystemRoleApi,
  role,
  groupedPermissions,
  isLoading,
  error,
  canAssignPermissions,
  draftPermissionKeys,
  isDirty,
  isSaving,
  isPermissionsUpdating,
  isRoleEditable,
  isPermissionDirty,
  isConfirmModalOpen,
  isEditableSystemRole,
  userRoleName,
  onTogglePermission,
  onSetAllPermissions,
  allPermissionsSelected,
  onConfirmChanges,
  onDiscardChanges,
  onConfirmModalClose,
  onConfirmSave,
  onBack,
  canEditName,
  draftName,
  isNameDirty,
  isSavingName,
  onNameChange,
  onDiscardName,
  onSaveName,
}: RoleDetailPageViewProps) {
  const { t } = useTranslation('authorization');

  const totalPermissionCount = useMemo(
    () =>
      groupedPermissions.reduce(
        (sum, group) => sum + group.permissions.length,
        0,
      ),
    [groupedPermissions],
  );

  if (!useSystemRoleApi && !organizationId) {
    return (
      <ErrorBox
        title={t('page.errorTitle')}
        description={t('page.selectOrganization')}
      />
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBox title={t('page.errorTitle')} description={error.message} />
    );
  }

  if (!role) {
    return (
      <ErrorBox
        title={t('page.errorTitle')}
        description={t('page.errorTitle')}
      />
    );
  }

  return (
    <Flex className="app-page authorization-page" vertical>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <RoleDetailHero
            role={role}
            useSystemRoleApi={useSystemRoleApi}
            assignedPermissionCount={draftPermissionKeys.length}
            totalPermissionCount={totalPermissionCount}
            userRoleName={userRoleName}
            isRoleEditable={isRoleEditable}
            canAssignPermissions={canAssignPermissions}
            canEditName={canEditName}
            draftName={draftName}
            isNameDirty={isNameDirty}
            isSavingName={isSavingName}
            onNameChange={onNameChange}
            onDiscardName={onDiscardName}
            onSaveName={onSaveName}
            onBack={onBack}
          />
        </Col>

        <Col span={24}>
          <Card className="authorization-section-card authorization-permissions-section">
            <Flex
              vertical
              gap={4}
              className="authorization-section-card-header"
            >
              <span className="authorization-section-card-title">
                {t('detail.permissionsTitle')}
              </span>
              <span className="authorization-section-card-subtitle">
                {t('detail.permissionsSubtitle')}
              </span>
            </Flex>

            <RolePermissionsAccordion
              groupedPermissions={groupedPermissions}
              draftPermissionKeys={draftPermissionKeys}
              isDirty={isDirty}
              isSaving={isSaving}
              isPermissionsUpdating={isPermissionsUpdating}
              isRoleEditable={isRoleEditable && canAssignPermissions}
              isPermissionDirty={isPermissionDirty}
              isConfirmModalOpen={isConfirmModalOpen}
              isEditableSystemRole={isEditableSystemRole}
              onTogglePermission={onTogglePermission}
              onSetAllPermissions={onSetAllPermissions}
              allPermissionsSelected={allPermissionsSelected}
              onConfirmChanges={onConfirmChanges}
              onDiscardChanges={onDiscardChanges}
              onConfirmModalClose={onConfirmModalClose}
              onConfirmSave={onConfirmSave}
            />
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}
